import { create } from 'zustand'
import { showNotification } from '../utils/notifications'
import { addDays, subMonths, format } from 'date-fns'

// Helper function to map objective values to user-friendly names
const mapObjective = (objective) => {
    const objectiveMap = {
        OUTCOME_AWARENESS: 'Awareness',
        OUTCOME_ENGAGEMENT: 'Engagement',
        OUTCOME_LEADS: 'Leads',
        OUTCOME_SALES: 'Sales',
        OUTCOME_TRAFFIC: 'Traffic',
    }

    return objectiveMap[objective] || objective
}

// Helper function to transform API data to match UI structure
const transformAdsManagerData = (apiData) => {
    if (!apiData || !apiData.length) return []

    // Filter out errors and only process successful campaign data
    const campaigns = apiData.filter((item) => item.__typename === 'AdsManagerCampaign')

    // Log any errors for debugging
    const errors = apiData.filter((item) => item.__typename === 'AdsManagerError')
    if (errors.length > 0) {
        console.error('Ads Manager Data Errors:', errors)
    }

    return campaigns.map((campaign) => {
        // Group ad sets by sales_funnel_stage
        const stageGroups = {}

        campaign.adSets.forEach((adSet) => {
            const stage = adSet.sales_funnel_stage || 'acquisition'
            if (!stageGroups[stage]) {
                stageGroups[stage] = []
            }

            // Transform adSet data
            const adSetInsights = adSet.insights && adSet.insights[0] ? adSet.insights[0] : {}
            const transformedAdSet = {
                id: adSet.id,
                db_id: adSet.db_id,
                name: adSet.name,
                active: adSet.status === 'ACTIVE',
                budget: adSet.daily_budget / 100, // Convert cents to dollars
                learning_phrase: getLearningPhase(adSet.learning_stage_info),
                results: mapObjective(campaign.objective),
                amount_spent: adSetInsights.spend || 0,
                roas: calculateROAS(adSetInsights),
                cost_per_purchase: calculateCostPerPurchase(adSetInsights),
                cpm: adSetInsights.cpm || 0,
                ctr: adSetInsights.ctr || 0,
                impressions: adSetInsights.impressions || 0,
                link_clicks: getLinkClicksFromInsights(adSetInsights),
                leads: getLeadsFromInsights(adSetInsights),
                cost_per_lead: calculateCostPerLead(adSetInsights.spend, getLeadsFromInsights(adSetInsights)),
                purchases: getPurchasesFromInsights(adSetInsights),
                revenue: getRevenueFromInsights(adSetInsights),
                latest_actions: adSet.latest_actions || [],
                // AI audience the ad set was created from (those ad sets only)
                ...(adSet.audience_hypothesis
                    ? { audience_hypothesis: adSet.audience_hypothesis, audience_mode: adSet.audience_mode || null }
                    : {}),
                // Who the ad set excludes (ad sets published with exclusions only)
                ...(adSet.exclusion_note ? { exclusion_note: adSet.exclusion_note } : {}),
                // Warm audience the ad set targets (lead form openers, or a longer window)
                ...(adSet.warm_audience_note ? { warm_audience_note: adSet.warm_audience_note } : {}),
                // Lookalike the ad set targets (seed, range, country and lead quality)
                ...(adSet.lookalike_note ? { lookalike_note: adSet.lookalike_note } : {}),
                // Funded by the campaign budget (Advantage+ leads); the ad set has no budget of its own
                ...(adSet.uses_campaign_budget ? { uses_campaign_budget: true } : {}),
                // Saved daily budget (minor units), sent back with an inline budget edit
                ...(adSet.stored_budget != null ? { stored_budget: adSet.stored_budget } : {}),
                // Added with a budget move that was not applied or may still be running: it must stay paused
                ...(adSet.budget_move_note ? { budget_move_note: adSet.budget_move_note } : {}),
                ads: adSet.ads.map((ad) => {
                    const adInsights = ad.insights && ad.insights[0] ? ad.insights[0] : {}
                    return {
                        id: ad.id,
                        db_id: ad.db_id,
                        ad_creative_id: ad.ad_creative_id,
                        name: ad.name,
                        active: ad.status === 'ACTIVE',
                        learning_phrase: '',
                        results: mapObjective(campaign.objective),
                        amount_spent: adInsights.spend || 0,
                        roas: calculateROAS(adInsights),
                        cost_per_purchase: calculateCostPerPurchase(adInsights),
                        cpm: adInsights.cpm || 0,
                        ctr: adInsights.ctr || 0,
                        impressions: adInsights.impressions || 0,
                        link_clicks: getLinkClicksFromInsights(adInsights),
                        leads: getLeadsFromInsights(adInsights),
                        cost_per_lead: calculateCostPerLead(adInsights.spend, getLeadsFromInsights(adInsights)),
                        purchases: getPurchasesFromInsights(adInsights),
                        revenue: getRevenueFromInsights(adInsights),
                        latest_actions: ad.latest_actions || [],
                        asset_path: null,
                    }
                }),
            }

            stageGroups[stage].push(transformedAdSet)
        })

        // Convert stage groups to stages array
        const stages = Object.entries(stageGroups).map(([stageName, adsets]) => ({
            name: stageName,
            adsets: adsets,
            ...(campaign.budget_mode ? { uses_campaign_budget: true } : {}),
        }))

        const campaignInsights = campaign.insights && campaign.insights[0] ? campaign.insights[0] : {}

        return {
            id: campaign.id,
            campaign_name: campaign.name,
            active: campaign.status === 'ACTIVE',
            status: campaign.status,
            effective_status: campaign.effective_status,
            error: campaign.error,
            // Ad sets of AI audiences that were not published (published campaigns only)
            ...(campaign.warning ? { warning: campaign.warning } : {}),
            // Delivery of the AI audience ad sets side by side (campaigns with such ad sets only)
            ...(campaign.audience_test ? { audience_test: campaign.audience_test } : {}),
            // One campaign budget (Advantage+ leads campaigns only), in major units, and the state Meta reported
            ...(campaign.budget_mode
                ? {
                      budget_mode: campaign.budget_mode,
                      campaign_budget: (campaign.campaign_daily_budget || 0) / 100,
                      advantage_leads: campaign.advantage_leads || null,
                  }
                : {}),
            objective: mapObjective(campaign.objective),
            results: mapObjective(campaign.objective),
            amount_spent: campaignInsights.spend || 0,
            roas: calculateROAS(campaignInsights),
            cost_per_purchase: calculateCostPerPurchase(campaignInsights),
            cpm: campaignInsights.cpm || 0,
            ctr: campaignInsights.ctr || 0,
            impressions: campaignInsights.impressions || 0,
            link_clicks: getLinkClicksFromInsights(campaignInsights),
            leads: getLeadsFromInsights(campaignInsights),
            cost_per_lead: calculateCostPerLead(campaignInsights.spend, getLeadsFromInsights(campaignInsights)),
            purchases: getPurchasesFromInsights(campaignInsights),
            revenue: getRevenueFromInsights(campaignInsights),
            latest_actions: campaign.latest_actions || [],
            stages: stages,
        }
    })
}

// Helper function to calculate cost per purchase
const calculateCostPerPurchase = (insights) => {
    if (!insights || !insights.actions || !insights.spend) return 0

    // Get total purchases (check both purchase and omni_purchase)
    const purchases = (insights.actions.purchase || 0) + (insights.actions.omni_purchase || 0)

    if (purchases === 0) return 0

    return insights.spend / purchases
}

// Cost per lead from spend and the leads shown in the Leads column; 0 (shown as '-') without spend or leads
export const calculateCostPerLead = (spend, leads) => {
    const amount = Number(spend)
    const count = Number(leads)
    if (!Number.isFinite(amount) || !Number.isFinite(count) || amount <= 0 || count <= 0) return 0

    return amount / count
}

// An ad set is an AI audience ad set when it was created from an audience hypothesis
export const isAiAdSet = (adSet) => !!adSet?.audience_hypothesis

export const AUDIENCE_TYPE_FILTERS = { ALL: 'all', AI: 'ai', EXISTING: 'existing' }

export const filterAdSetsByAudienceType = (adSets, audienceType = AUDIENCE_TYPE_FILTERS.ALL) => {
    if (!adSets || audienceType === AUDIENCE_TYPE_FILTERS.ALL) return adSets
    if (audienceType === AUDIENCE_TYPE_FILTERS.AI) return adSets.filter(isAiAdSet)
    if (audienceType === AUDIENCE_TYPE_FILTERS.EXISTING) return adSets.filter((adSet) => !isAiAdSet(adSet))
    return adSets
}

// Spend, leads, cost per lead and budget share of the AI and the existing ad sets of a campaign. Null unless the
// campaign has both kinds. Ad sets funded by the campaign budget have no budget of their own, so they add no budget.
export const computeAudienceTypeSubtotals = (campaign) => {
    const adSets = (campaign?.stages || []).flatMap((stage) => stage.adsets || [])
    const summarize = (group) => {
        const budget = group.reduce((sum, adSet) => sum + (adSet.uses_campaign_budget ? 0 : adSet.budget || 0), 0)
        const amount_spent = group.reduce((sum, adSet) => sum + (adSet.amount_spent || 0), 0)
        const leads = group.reduce((sum, adSet) => sum + (adSet.leads || 0), 0)
        return {
            count: group.length,
            budget,
            amount_spent,
            leads,
            cost_per_lead: calculateCostPerLead(amount_spent, leads),
        }
    }

    const ai = adSets.filter(isAiAdSet)
    const existing = adSets.filter((adSet) => !isAiAdSet(adSet))
    if (!ai.length || !existing.length) return null

    const subtotals = { ai: summarize(ai), existing: summarize(existing) }
    const totalBudget = subtotals.ai.budget + subtotals.existing.budget
    Object.values(subtotals).forEach((subtotal) => {
        subtotal.budget_share = totalBudget > 0 ? (subtotal.budget / totalBudget) * 100 : null
    })

    return subtotals
}

// Helper function to get link clicks from insights
const getLinkClicksFromInsights = (insights) => {
    if (!insights || !insights.actions) return 0

    const linkClicks = insights.actions.link_click
    return linkClicks ? parseInt(linkClicks) || 0 : 0
}

// Helper function to get leads from insights
const getLeadsFromInsights = (insights) => {
    if (!insights || !insights.actions) return 0

    const leads = insights.actions.lead
    return leads ? parseInt(leads) || 0 : 0
}

// Helper function to get purchases from insights
const getPurchasesFromInsights = (insights) => {
    if (!insights || !insights.actions) return 0

    const purchases = insights.actions.purchase
    return purchases ? parseInt(purchases) || 0 : 0
}

// Helper function to get revenue from insights
const getRevenueFromInsights = (insights) => {
    if (!insights || !insights.action_values) return 0

    const revenue = insights.action_values.purchase
    return revenue ? parseFloat(revenue) || 0 : 0
}

// Helper function to calculate ROAS (Return on Ad Spend)
// ROAS = Purchase value (revenue) / Ad spend
const calculateROAS = (insights) => {
    if (!insights || !insights.spend || insights.spend === 0) {
        return 0
    }

    // If Meta provided purchase_roas, use it
    if (insights.purchase_roas !== undefined && insights.purchase_roas !== null) {
        return insights.purchase_roas
    }

    // Otherwise calculate from purchase value and spend
    if (!insights.action_values) return 0

    const purchaseValue = (insights.action_values.purchase || 0) + (insights.action_values.omni_purchase || 0)

    if (purchaseValue === 0) {
        return 0
    }

    return purchaseValue / insights.spend
}

// Helper function to extract learning stage from learning_stage_info
export const getLearningPhase = (learningStageInfo) => {
    // Map learning stage info to user-friendly display names
    const learningPhaseMap = {
        LEARNING: 'Learning',
        COMPLETED: 'Completed',
        LIMITED_LEARNING: 'Limited Learning',
    }

    return learningPhaseMap[learningStageInfo] || learningStageInfo || ''
}

export const applyOrder = (campaigns, orderedIds) => {
    if (!orderedIds?.length) return campaigns
    const byId = new Map(campaigns.map((c) => [c.id, c]))
    const ordered = []
    const seen = new Set()
    for (const id of orderedIds) {
        const c = byId.get(id)
        if (c && !seen.has(id)) {
            ordered.push(c)
            seen.add(id)
        }
    }
    const remaining = campaigns.filter((c) => !seen.has(c.id))
    return [...ordered, ...remaining]
}

// Move `sourceId` adjacent to `targetId`. `position` is 'before' or 'after'.
// Returns a new array; preserves original input when ids are unknown or the
// move would be a no-op.
export const reorderById = (campaigns, sourceId, targetId, position = 'after') => {
    if (!sourceId || !targetId || sourceId === targetId) return campaigns
    const from = campaigns.findIndex((c) => c.id === sourceId)
    const to = campaigns.findIndex((c) => c.id === targetId)
    if (from < 0 || to < 0) return campaigns
    const next = [...campaigns]
    const [moved] = next.splice(from, 1)
    // After removal, the target's index shifts down by 1 if it was after the source.
    const adjustedTo = from < to ? to - 1 : to
    const insertAt = position === 'after' ? adjustedTo + 1 : adjustedTo
    if (insertAt === from) return campaigns
    next.splice(insertAt, 0, moved)
    return next
}

// Helper function to get dates for default custom range
const getDefaultCustomDates = () => {
    const today = new Date()
    const tomorrow = addDays(today, 1)
    const oneMonthAgo = subMonths(today, 1)

    return {
        startDate: format(oneMonthAgo, 'yyyy-MM-dd'),
        endDate: format(tomorrow, 'yyyy-MM-dd'),
    }
}

// Sum and average the metrics of ad sets (stage rows, and campaign rows under an audience type filter)
export const computeStageMetrics = (stage) => {
    const { adsets } = stage
    if (!adsets || adsets.length === 0) {
        return {
            budget: 0,
            amount_spent: 0,
            roas: 0,
            cost_per_purchase: 0,
            cpm: 0,
            ctr: 0,
            impressions: 0,
            link_clicks: 0,
            leads: 0,
            cost_per_lead: 0,
            purchases: 0,
            revenue: 0,
        }
    }

    // Sum metrics for budget, amount_spent, impressions, link_clicks
    const budget = adsets.reduce((sum, adset) => sum + (adset.budget || 0), 0)
    const amount_spent = adsets.reduce((sum, adset) => sum + (adset.amount_spent || 0), 0)
    const impressions = adsets.reduce((sum, adset) => sum + (adset.impressions || 0), 0)
    const link_clicks = adsets.reduce((sum, adset) => sum + (adset.link_clicks || 0), 0)
    const leads = adsets.reduce((sum, adset) => sum + (adset.leads || 0), 0)
    const purchases = adsets.reduce((sum, adset) => sum + (adset.purchases || 0), 0)
    const revenue = adsets.reduce((sum, adset) => sum + (adset.revenue || 0), 0)

    // Calculate ROAS as total revenue / total spend (for all adsets)
    const roas = amount_spent > 0 ? revenue / amount_spent : 0

    // Calculate cost per purchase from totals
    const cost_per_purchase = purchases > 0 ? amount_spent / purchases : 0

    // For CPM and CTR, only count adsets that have actual data
    const adsetsWithCPM = adsets.filter((adset) => adset.cpm !== undefined && adset.cpm !== null && adset.cpm > 0)
    const cpmCount = adsetsWithCPM.length || 1
    const cpm = adsetsWithCPM.reduce((sum, adset) => sum + adset.cpm, 0) / cpmCount

    const adsetsWithCTR = adsets.filter((adset) => adset.ctr !== undefined && adset.ctr !== null && adset.ctr > 0)
    const ctrCount = adsetsWithCTR.length || 1
    const ctr = adsetsWithCTR.reduce((sum, adset) => sum + adset.ctr, 0) / ctrCount

    return {
        budget,
        amount_spent,
        roas,
        cost_per_purchase,
        cpm,
        ctr,
        impressions,
        link_clicks,
        leads,
        cost_per_lead: calculateCostPerLead(amount_spent, leads),
        purchases,
        revenue,
    }
}

const AGGREGATED_METRICS = [
    'amount_spent',
    'roas',
    'cost_per_purchase',
    'cpm',
    'ctr',
    'impressions',
    'link_clicks',
    'leads',
    'cost_per_lead',
    'purchases',
    'revenue',
]

// The campaign as seen through the audience type filter: only matching ad sets, stages without any left are dropped
// and the metrics are those of the matching ad sets. Without a filter the campaign itself is returned.
export const applyAudienceTypeFilter = (campaign, audienceType = AUDIENCE_TYPE_FILTERS.ALL) => {
    if (audienceType === AUDIENCE_TYPE_FILTERS.ALL || !campaign?.stages) return campaign

    const stages = campaign.stages
        .map((stage) => ({ ...stage, adsets: filterAdSetsByAudienceType(stage.adsets, audienceType) }))
        .filter((stage) => stage.adsets.length > 0)
    const adsets = stages.flatMap((stage) => stage.adsets)
    const metrics = computeStageMetrics({ adsets })
    // Ratios come from the summed figures (CPM = spend per thousand impressions, CTR weighted by impressions), as a
    // campaign-level value would be, not from the mean of ad set values
    const impressions = metrics.impressions
    metrics.cpm = impressions > 0 ? (metrics.amount_spent / impressions) * 1000 : 0
    metrics.ctr =
        impressions > 0
            ? adsets.reduce((sum, adset) => sum + (adset.ctr || 0) * (adset.impressions || 0), 0) / impressions
            : 0

    return {
        ...campaign,
        stages,
        ...Object.fromEntries(AGGREGATED_METRICS.map((key) => [key, metrics[key]])),
    }
}

const useAdsManagerStore = create((set, get) => ({
    campaigns: [],
    dateRange: {
        type: 'custom',
        ...getDefaultCustomDates(),
    },
    searchTerm: '',
    audienceTypeFilter: AUDIENCE_TYPE_FILTERS.ALL,
    expandedItems: new Set(),
    loading: false,
    error: null,
    publisherErrors: [], // Track publisher-specific errors
    lastErrorMessage: null, // Track last error to prevent duplicate notifications
    loadingItems: new Set(), // Track which items are currently being toggled

    setDateRange: (dateRange) => set({ dateRange }),

    setSearchTerm: (searchTerm) => set({ searchTerm }),

    setAudienceTypeFilter: (audienceTypeFilter) => set({ audienceTypeFilter }),

    toggleItemExpanded: (itemId) =>
        set((state) => {
            const newExpanded = new Set(state.expandedItems)
            if (newExpanded.has(itemId)) {
                newExpanded.delete(itemId)
            } else {
                newExpanded.add(itemId)
            }
            return { expandedItems: newExpanded }
        }),

    // Add item to loading state
    setItemLoading: (itemId, isLoading) =>
        set((state) => {
            const newLoadingItems = new Set(state.loadingItems)
            if (isLoading) {
                newLoadingItems.add(itemId)
            } else {
                newLoadingItems.delete(itemId)
            }
            return { loadingItems: newLoadingItems }
        }),

    // Check if item is loading
    isItemLoading: (itemId) => {
        const { loadingItems } = get()
        return loadingItems.has(itemId)
    },

    updateBudget: (adsetId, newBudget, parentIds) =>
        set((state) => {
            const campaigns = [...state.campaigns]
            const campaign = campaigns.find((c) => c.campaign_name === parentIds.campaign)

            if (campaign) {
                const stage = campaign.stages.find((s) => s.name === parentIds.stage)
                if (stage) {
                    const adset = stage.adsets.find((a) => a.name === adsetId)
                    if (adset) {
                        adset.budget = parseInt(newBudget) || 0
                    }
                }
            }

            return { campaigns }
        }),

    // Set campaigns data from API
    setCampaigns: (apiData, orderedIds = []) => {
        // Extract errors before transformation
        const errors = apiData.filter((item) => item.__typename === 'AdsManagerError')
        const transformedData = transformAdsManagerData(apiData)

        set({
            campaigns: applyOrder(transformedData, orderedIds),
            publisherErrors: errors,
        })

        // Show single error notification if any errors exist (only if different from last error)
        if (errors.length > 0) {
            const errorMessage =
                errors.length === 1
                    ? errors[0].error // Single error: show exact message
                    : `${errors[0].error} (${errors.length} failures)` // Multiple: show first + count

            // Only show notification if error message is different from last time
            const currentState = get()
            if (errorMessage !== currentState.lastErrorMessage) {
                showNotification(
                    errorMessage,
                    'error',
                    0, // No auto-close
                    'Retry', // Button text
                    () => get().refreshData(), // Retry callback
                )

                // Update last error message
                set({ lastErrorMessage: errorMessage })
            }
        } else {
            // Clear last error message when no errors
            set({ lastErrorMessage: null })
        }
    },

    // Reorder campaigns by moving `sourceId` adjacent to `targetId`
    // (used by drag-and-drop and keyboard reorder).
    reorderCampaigns: (sourceId, targetId, position = 'after') =>
        set((state) => ({
            campaigns: reorderById(state.campaigns, sourceId, targetId, position),
        })),

    // Set loading state
    setLoading: (loading) => set({ loading }),

    // Set error state
    setError: (error) => set({ error }),

    refreshData: () => {
        console.log('Refreshing data...')
        // This will be called from the component to trigger a refetch
    },

    // Compute stage metrics from adsets
    computeStageMetrics: (stage) => computeStageMetrics(stage),

    getFilteredCampaigns: () => {
        const { campaigns, searchTerm, audienceTypeFilter } = get()
        if (!searchTerm) return campaigns

        return campaigns.filter((campaign) => {
            const searchLower = searchTerm.toLowerCase()
            return (
                campaign.campaign_name.toLowerCase().includes(searchLower) ||
                campaign.stages.some(
                    (stage) =>
                        stage.name.toLowerCase().includes(searchLower) ||
                        filterAdSetsByAudienceType(stage.adsets, audienceTypeFilter).some(
                            (adset) =>
                                adset.name.toLowerCase().includes(searchLower) ||
                                adset.ads?.some((ad) => ad.name.toLowerCase().includes(searchLower)),
                        ),
                )
            )
        })
    },

    // Add a processing campaign to the campaigns list
    addProcessingCampaign: (campaignData) =>
        set((state) => {
            // Check if campaign already exists
            const existingIndex = state.campaigns.findIndex((c) => c.id === campaignData.id)

            if (existingIndex !== -1) {
                // Update existing campaign
                const updatedCampaigns = [...state.campaigns]
                updatedCampaigns[existingIndex] = {
                    ...updatedCampaigns[existingIndex],
                    status: campaignData.status,
                    effective_status: campaignData.status,
                    error: campaignData.error,
                }
                return { campaigns: updatedCampaigns }
            } else {
                // Add new processing campaign
                const processingCampaign = {
                    id: campaignData.id,
                    campaign_name: campaignData.name,
                    active: false, // Processing campaigns are not active
                    status: campaignData.status,
                    effective_status: campaignData.status,
                    error: campaignData.error,
                    objective: '',
                    results: '',
                    amount_spent: 0,
                    roas: 0,
                    cost_per_purchase: 0,
                    cpm: 0,
                    ctr: 0,
                    impressions: 0,
                    link_clicks: 0,
                    leads: 0,
                    cost_per_lead: 0,
                    purchases: 0,
                    revenue: 0,
                    stages: [], // No stages for processing campaigns
                }

                return { campaigns: [processingCampaign, ...state.campaigns] }
            }
        }),
}))

export default useAdsManagerStore
